import { useEffect, useState } from "react";

import {
    getGoogleDmaConversions,
    postGoogleDmaMappings,
    postSaveGoogleDma,
} from "helpers/destinations/googleDma";

import useHostFooterAction from "components/organisms/CreationModal/hooks/useHostFooterAction";
import {
    areGoogleAdsMappingsValid,
    buildSavePayload,
    getConversionsFetchErrorMessage,
    isGoogleAdsConversionCode,
    normalizeAutoMatchedEvents,
} from "components/organisms/Destinations/GoogleAdsConversion/GoogleAdsConversion.helpers";

import { findConversionName } from "../DestinationCreation.helpers";

/**
 * Google Ads Website / Offline / Lead / Store Sales conversions (google-dma):
 * account select, save, then map events to conversion actions.
 */
const useGoogleAdsConversionDestination = ({
    language,
    destinationCode,
    setSaveDisabled,
    setModalLoading,
    completeCreation,
    hostActions,
    registerFooterAction,
}) => {
    const [data, setData] = useState(null);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [destinationId, setDestinationId] = useState(null);
    const [conversions, setConversions] = useState([]);
    const [events, setEvents] = useState([]);
    const [mappings, setMappings] = useState([]);
    const [conversionsError, setConversionsError] = useState(null);
    const [isMappingOpen, setIsMappingOpen] = useState(false);
    const [isMappingCompleted, setIsMappingCompleted] = useState(false);

    const isConversionType = isGoogleAdsConversionCode(destinationCode);

    useEffect(() => {
        if (!isConversionType || isMappingOpen) return;

        setSaveDisabled(
            !(
                isAuthenticated &&
                data?.selectedAccount?.accountId &&
                data?.partnerInstallId
            ),
        );
    }, [
        isConversionType,
        isMappingOpen,
        isAuthenticated,
        data?.selectedAccount?.accountId,
        data?.partnerInstallId,
        setSaveDisabled,
    ]);

    useEffect(() => {
        if (!isConversionType || !isMappingOpen) return;

        const hasFetchError = !!conversionsError;
        const hasEmptyConversions = !hasFetchError && !conversions?.length;

        setSaveDisabled(
            hasFetchError ||
                hasEmptyConversions ||
                !areGoogleAdsMappingsValid(
                    mappings,
                    conversions,
                    destinationCode,
                ),
        );
    }, [
        isConversionType,
        isMappingOpen,
        conversionsError,
        conversions,
        mappings,
        destinationCode,
        setSaveDisabled,
    ]);

    const resetOnTypeChange = () => {
        setData(null);
        setIsAuthenticated(false);
        setDestinationId(null);
        setConversions([]);
        setEvents([]);
        setMappings([]);
        setConversionsError(null);
        setIsMappingOpen(false);
        setIsMappingCompleted(false);
    };

    const loadConversions = async (createdId) => {
        const response = await getGoogleDmaConversions(createdId);

        if (!response.status) {
            setConversionsError(
                getConversionsFetchErrorMessage(
                    destinationCode,
                    response.message,
                ),
            );
            setConversions([]);
            setEvents([]);
            setMappings([]);
            return;
        }

        const { all_conversions, all_events, auto_matched_events } =
            response.data?.conversions ?? {};

        setConversionsError(null);
        setConversions(all_conversions || []);
        setEvents(all_events || []);
        setMappings(normalizeAutoMatchedEvents(auto_matched_events));
    };

    const retryConversions = async () => {
        if (!destinationId) return;
        setModalLoading(true);
        await loadConversions(destinationId);
        setModalLoading(false);
    };

    const create = async () => {
        setModalLoading(true);

        const account = data?.selectedAccount;
        const partnerInstallId = data?.partnerInstallId;

        if (!account?.accountId || !partnerInstallId) {
            setModalLoading(false);
            return;
        }

        const response = await postSaveGoogleDma(
            buildSavePayload({
                account,
                partnerInstallId,
                type: destinationCode,
            }),
        );

        const createdId = response.data?.id ?? response.data?.destination_id;

        if (!response.status || !createdId) {
            setModalLoading(false);
            return;
        }

        setDestinationId(createdId);
        setData(null);
        setIsAuthenticated(false);

        await loadConversions(createdId);

        setIsMappingOpen(true);
        setModalLoading(false);
    };

    const saveMappings = async () => {
        if (
            !areGoogleAdsMappingsValid(mappings, conversions, destinationCode)
        ) {
            return;
        }

        setModalLoading(true);
        try {
            if (destinationId && mappings?.length) {
                const response = await postGoogleDmaMappings(destinationId, {
                    mappings: mappings.map((m) => ({
                        event: m.event?.toString(),
                        conversion_id: m.conversion_id?.toString(),
                        conversion_name:
                            m.conversion_name ||
                            findConversionName(conversions, m.conversion_id),
                    })),
                });

                if (!response.status) {
                    setModalLoading(false);
                    return;
                }

                setIsMappingCompleted(true);
                await completeCreation(destinationId);
            }
        } finally {
            setModalLoading(false);
            setIsMappingOpen(false);
        }
    };

    const closeMapping = () => {
        const hasFetchError = !!conversionsError;
        const hasEmptyConversions = !hasFetchError && !conversions?.length;

        setIsMappingOpen(false);

        if (!isMappingCompleted && (hasEmptyConversions || hasFetchError)) {
            completeCreation(destinationId);
            return;
        }

        hostActions.refresh();
        hostActions.close();
    };

    const canCreate = !!(
        isAuthenticated &&
        data?.selectedAccount?.accountId &&
        data?.partnerInstallId
    );
    const hasFetchError = !!conversionsError;
    const hasEmptyConversions = !hasFetchError && !conversions?.length;
    const canSaveMappings =
        !hasFetchError &&
        !hasEmptyConversions &&
        areGoogleAdsMappingsValid(mappings, conversions, destinationCode);
    const createLabel =
        language?.buttons?.create_destination ?? "Create destination";
    const saveLabel = language?.buttons?.save ?? "Save";

    useHostFooterAction(
        isConversionType ? registerFooterAction : undefined,
        `${destinationCode}:form`,
        {
            label: createLabel,
            onSubmit: create,
            isDisabled: !canCreate,
            hidden: isMappingOpen,
        },
    );
    useHostFooterAction(
        isConversionType ? registerFooterAction : undefined,
        `${destinationCode}:mapping`,
        {
            label: saveLabel,
            onSubmit: saveMappings,
            isDisabled: !canSaveMappings,
            hidden: !isMappingOpen,
        },
    );

    return {
        isConversionType,
        setData,
        setIsAuthenticated,
        conversions,
        events,
        mappings,
        setMappings,
        conversionsError,
        isMappingOpen,
        resetOnTypeChange,
        retryConversions,
        create,
        saveMappings,
        closeMapping,
    };
};

export default useGoogleAdsConversionDestination;
